/**
 * Minimal allowlist HTML sanitizer for LLM-generated rich text (e.g. tone of
 * voice). It never reuses the original nodes: it rebuilds a fresh DOM tree
 * containing only allowed tags, so scripts, event handlers, inline styles and
 * unknown attributes can't survive.
 */

const ALLOWED_TAGS = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
]);

// Elements whose content must be dropped entirely (not unwrapped).
const DROP_WITH_CONTENT = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'noscript',
  'template',
  'svg',
  'math',
]);

// Only these class names are kept, so clients can't inject arbitrary classes.
const ALLOWED_CLASSES = new Set(['tom-de-voz-titulo', 'tom-de-voz-texto']);

const HTML_TAG_REGEX = /<\/?(p|br|strong|b|em|i|u|ul|ol|li|h[1-6])\b[^>]*>/i;

export function isHtmlString(value?: string | null): value is string {
  return typeof value === 'string' && HTML_TAG_REGEX.test(value);
}

function sanitizeNode(source: Node, target: Node, doc: Document) {
  source.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(doc.createTextNode(child.textContent ?? ''));
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = child as Element;
    const tag = element.tagName.toLowerCase();

    if (DROP_WITH_CONTENT.has(tag)) {
      return;
    }

    if (!ALLOWED_TAGS.has(tag)) {
      // Unknown tag: keep its (sanitized) children, drop the tag itself.
      sanitizeNode(element, target, doc);
      return;
    }

    const clean = doc.createElement(tag);

    const classes = (element.getAttribute('class') ?? '')
      .split(/\s+/)
      .filter((name) => ALLOWED_CLASSES.has(name));

    if (classes.length > 0) {
      clean.setAttribute('class', classes.join(' '));
    }

    if (tag !== 'br') {
      sanitizeNode(element, clean, doc);
    }

    target.appendChild(clean);
  });
}

export function sanitizeHtml(html: string): string {
  if (typeof DOMParser === 'undefined') {
    return '';
  }

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const container = parsed.createElement('div');

  sanitizeNode(parsed.body, container, parsed);

  return container.innerHTML;
}
